import type { ReactNode } from 'react';
import { useTranslations } from '@/i18n/translations';
import { Button, Pill, Row, type Tone} from '@leanin/ui';

export type AppliedFilterChip = {
  id: string;
  label: ReactNode;
  onRemove: () => void;
  tone?: Tone;
  ariaLabel?: string;
};

export type AppliedFiltersProps = {
  chips: ReadonlyArray<AppliedFilterChip>;
  onClearAll?: () => void;
  clearAllLabel?: string;
};

export function AppliedFilters({
  chips,
  onClearAll,
  clearAllLabel,
}: AppliedFiltersProps) {
  const t = useTranslations('Common');
  if (chips.length === 0) return null;
  return (
    <Row
      gap="xs"
      wrap
      align="center"
      style={{
        // eslint-disable-next-line @leanin/ui/no-inline-spacing-vars -- Row primitive accepts gap but not rowGap; wrapping case
        rowGap: 'var(--space-xs)',
      }}
    >
      {chips.map((chip) => (
        <Pill
          key={chip.id}
          size="md"
          tone={chip.tone ?? 'neutral'}
          onRemove={chip.onRemove}
          {...(chip.ariaLabel ? { removeAriaLabel: chip.ariaLabel } : {})}
        >
          {chip.label}
        </Pill>
      ))}
      {onClearAll && chips.length >= 2 ? (
        <Button variant="ghost" size="sm" onClick={onClearAll}>
          {clearAllLabel ?? t('widgets.filters.clearAll')}
        </Button>
      ) : null}
    </Row>
  );
}
